import type {
  GameContext,
  GameInstance,
  GameModule,
  PreloadContext,
} from '@arcraz/arcadia-types';
import { DefenseGridInstance } from './instance.js';
import { PALETTE_HINT } from './palette.js';

/**
 * DEFENSE GRID — thirteenth cartridge (sc-3959).
 *
 * Missile Command (1980) heritage. Fixed-screen pointer-aim defense:
 * player has a cursor over a city skyline + 3 missile bases. Incoming
 * ICBMs arc down from the sky targeting cities; player clicks to
 * launch a counter-missile that explodes at the click point, killing
 * any incoming missile in the blast radius. Lose cities → game over.
 *
 * **First cartridge to use the pointer infrastructure** (see
 * apps/shell/src/runtime/input-pointer.ts and InputState.pointer).
 * Without that runtime work, this cartridge would have to fall back
 * to an analog-stick virtual cursor, which doesn't read as Missile
 * Command. Server-side replay validation is deferred until the
 * replay schema v2 epic (sc-3958) lands; until then scores will
 * post with `validation_status: flagged` rather than `accepted`.
 *
 * MVP build is a 6-step sequence (revised plan — each step ships
 * mechanic AND texture so the cartridge doesn't feel thin):
 *
 *   1. Skeleton — night sky + twinkling stars + 6 city silhouettes
 *      with flickering windows + 3 missile bases with turrets that
 *      aim at the cursor + pulsing crosshair + sentinel hint when
 *      no pointer has been touched + attract-mode demo loop after
 *      10s idle. **(this commit)**
 *   2. Counter-missiles — click launches an ascending arc from the
 *      nearest base; explodes at click point with blast radius.
 *   3. Incoming ICBM waves — magenta trails arc down from the top
 *      toward target cities. Deterministic spawn timing from PRNG.
 *   4. Interception + city damage — counter-missile blasts kill
 *      incoming ICBMs in radius; cities destroyed when ICBMs land.
 *      Game-over when all cities are gone.
 *   5. Wave structure + scoring — per-wave difficulty ramp, wave-
 *      clear card with cities/ammo bonuses.
 *   6. Polish + determinism test — 1000-run test, TREASURE!-tier
 *      win celebration at wave 10, V011 migration registry entry.
 *
 * Resolution: 480×270 pixelated. Night-sky navy palette with cyan
 * defender + magenta threat sprites for high-contrast battle reads.
 */
const module_: GameModule = {
  id: 'defense-grid',
  title: 'DEFENSE GRID',
  manufacturer: 'ARCADIA AMUSEMENTS',
  year: 1984,
  genre: 'arena',
  resolution: { w: 480, h: 270 },
  pixelated: true,
  paletteHint: PALETTE_HINT,
  validatorVersion: 1,
  controls: [
    { keys: 'MOUSE / TOUCH', action: 'AIM' },
    { keys: 'CLICK / TAP', action: 'FIRE COUNTER-MISSILE' },
    { keys: 'Z', action: 'RESTART (AT GAME OVER)' },
  ],

  async preload(ctx: PreloadContext): Promise<void> {
    // SFX recipes — registered now even though step 1 doesn't fire
    // any. Step 2+ will use them. Keeping them here means each step
    // commit only touches gameplay code, not audio plumbing.

    // Counter-missile launch — short "thwip" rising blip on each
    // player click.
    ctx.registerSfx('counter-launch', {
      wave: 'triangle',
      pitch: [
        { t: 0, f: 320 },
        { t: 20, f: 640 },
      ],
      gain: [
        { t: 0, g: 0 },
        { t: 3, g: 0.18 },
        { t: 20, g: 0.0 },
      ],
    });
    // Explosion — short noise burst with sub-bass thump. Fires on
    // both player and ICBM explosions; the visual distinguishes them.
    ctx.registerSfx('boom', {
      wave: 'noise',
      pitch: [
        { t: 0, f: 200 },
        { t: 120, f: 50 },
      ],
      gain: [
        { t: 0, g: 0 },
        { t: 4, g: 0.45 },
        { t: 120, g: 0.0 },
      ],
    });
    // Incoming ICBM whine — descending sawtooth (we'll play this on
    // ICBM spawn in step 3 so the player gets an audio alert).
    ctx.registerSfx('incoming', {
      wave: 'sawtooth',
      pitch: [
        { t: 0, f: 880 },
        { t: 200, f: 220 },
      ],
      gain: [
        { t: 0, g: 0 },
        { t: 6, g: 0.20 },
        { t: 200, g: 0.0 },
      ],
    });
    // City destruction — deeper boom than 'boom', with longer tail.
    // Plays when an ICBM successfully lands on a city.
    ctx.registerSfx('city-destroyed', {
      wave: 'noise',
      pitch: [
        { t: 0, f: 140 },
        { t: 220, f: 40 },
      ],
      gain: [
        { t: 0, g: 0 },
        { t: 6, g: 0.55 },
        { t: 220, g: 0.0 },
      ],
    });
  },

  init(ctx: GameContext): GameInstance {
    return new DefenseGridInstance(ctx);
  },
};

export default module_;
